import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatMYR } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell,
    CartesianGrid,
} from "recharts";
import { TrendingUp, Package, ShoppingBag, AlertTriangle, Coins, Wallet, Cake, MessageCircle, Truck, HandCoins, ArrowRight } from "lucide-react";
import DateRangePicker, { toIsoDate } from "../components/DateRangePicker";

const PLATFORM_COLORS = ["#B08A3E", "#2563EB", "#F59E0B", "#DC2626", "#7C3AED", "#0891B2", "#DB2777", "#65A30D"];

export default function DashboardPage() {
    const [data, setData] = useState(null);
    const [birthdays, setBirthdays] = useState([]);
    const [range, setRange] = useState(undefined);

    useEffect(() => {
        const params = {};
        if (range?.from) params.start_date = toIsoDate(range.from);
        if (range?.to) params.end_date = toIsoDate(range.to);
        else if (range?.from) params.end_date = toIsoDate(range.from);
        api.get("/dashboard/summary", { params }).then((r) => setData(r.data));
    }, [range]);

    useEffect(() => {
        api.get("/customers/upcoming-birthdays", { params: { days: 14 } })
            .then((r) => setBirthdays(r.data))
            .catch(() => setBirthdays([]));
    }, []);

    if (!data) return <div className="text-gold-500">加载中...</div>;

    const stats = [
        { label: "销售额 (毛)", value: formatMYR(data.total_revenue), icon: TrendingUp, color: "text-gold-600", bg: "bg-gold-50", testId: "stat-gross" },
        { label: "销售成本", value: formatMYR(data.total_cost || 0), icon: Coins, color: "text-amber-600", bg: "bg-amber-50", testId: "stat-cost" },
        { label: "净利润 (扣成本)", value: formatMYR(data.total_profit || 0), icon: Wallet, color: "text-teal-700", bg: "bg-teal-50", testId: "stat-profit" },
        { label: "区间订单", value: data.total_orders, icon: ShoppingBag, color: "text-blue-600", bg: "bg-blue-50", testId: "stat-orders" },
        { label: "产品数量", value: data.total_products, icon: Package, color: "text-slate-700", bg: "bg-slate-100", testId: "stat-products" },
        { label: "低库存预警", value: data.low_stock_count, icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50", testId: "stat-lowstock" },
    ];

    const trendData = data.revenue_trend.map((d) => ({ date: d.date.slice(5), revenue: Number(d.revenue.toFixed(2)) }));
    const platform = (data.platform_breakdown || []).map((p) => ({ ...p, revenue: Number(p.revenue.toFixed(2)) }));
    const platformTotal = platform.reduce((s, p) => s + p.revenue, 0) || 1;

    return (
        <div className="space-y-8" data-testid="dashboard-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-gold-500">概览</div>
                    <h1 className="text-3xl font-bold tracking-tight text-slate-900 mt-1">仪表板</h1>
                    <p className="text-sm text-gold-500 mt-1">实时了解你的库存与销售动态</p>
                </div>
                <DateRangePicker value={range} onChange={setRange} />
            </div>

            {/* --- Today's todos --- */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="todo-cards">
                <TodoCard
                    testId="todo-shipments"
                    to="/sales?status=pending"
                    icon={Truck}
                    label="待处理订单"
                    count={data.pending_shipments_count || 0}
                    hint={data.pending_shipments_count ? `最近：${(data.pending_shipments_preview?.[0]?.invoice_no) || "查看列表"}` : "全部已完成 ✓"}
                    accent="text-orange-700 bg-orange-50 border-orange-200"
                    emptyAccent="text-emerald-700 bg-emerald-50 border-emerald-200"
                />
                <TodoCard
                    testId="todo-unpaid"
                    to="/payouts"
                    icon={HandCoins}
                    label="未收分成"
                    count={data.unpaid_payout_count || 0}
                    hint={data.unpaid_payout_count ? `合计 ${formatMYR(data.unpaid_payout_amount || 0)}` : "都已到账 ✓"}
                    accent="text-red-700 bg-red-50 border-red-200"
                    emptyAccent="text-emerald-700 bg-emerald-50 border-emerald-200"
                />
                <TodoCard
                    testId="todo-lowstock"
                    to="/products"
                    icon={AlertTriangle}
                    label="库存告急"
                    count={data.low_stock_count || 0}
                    hint={data.low_stock_count ? `${data.low_stock_items?.[0]?.name || ""} 等` : "库存充足 ✓"}
                    accent="text-amber-700 bg-amber-50 border-amber-200"
                    emptyAccent="text-emerald-700 bg-emerald-50 border-emerald-200"
                />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {stats.map((s) => (
                    <Card key={s.label} data-testid={s.testId} className="p-5 border-slate-200 shadow-sm hover:shadow transition-shadow duration-150">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="text-xs font-semibold uppercase tracking-wider text-gold-500 truncate">{s.label}</div>
                                <div className="text-2xl font-bold font-mono text-slate-900 mt-2 break-all">{s.value}</div>
                            </div>
                            <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${s.bg}`}>
                                <s.icon className={`h-4 w-4 ${s.color}`} />
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            {birthdays.length > 0 && (
                <Card data-testid="birthday-reminder-card" className="p-5 border-gold-200 shadow-sm" style={{ background: "linear-gradient(135deg, #FBF7EF 0%, #F5EBD1 100%)" }}>
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-gold-100 flex items-center justify-center">
                                <Cake className="h-4 w-4 text-gold-700" />
                            </div>
                            <div>
                                <div className="font-semibold text-slate-900">近期生日 / 纪念日</div>
                                <div className="text-xs text-gold-600">未来 14 天 · 提前联系老客户</div>
                            </div>
                        </div>
                        <Badge className="bg-gold-600 text-white border-0 font-mono">{birthdays.length}</Badge>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {birthdays.slice(0, 6).map((b) => {
                            const kindEmoji = b.kind === "birthday" ? "🎂" : "💍";
                            const kindLabel = b.kind === "birthday" ? "生日" : "纪念日";
                            const dayText = b.days_until === 0 ? "🎉 就是今天！" : b.days_until === 1 ? "明天" : `还有 ${b.days_until} 天`;
                            const message = encodeURIComponent(
                                b.kind === "birthday"
                                    ? `Hi ${b.name}，福缘珠宝祝您生日快乐！🎂✨ 感谢您一路的支持，特别为您准备了生日专属优惠，请回复领取～`
                                    : `Hi ${b.name}，福缘珠宝送上纪念日祝福！💍 愿这份美好一直延续，我们为您准备了专属礼遇～`
                            );
                            const rawPhone = (b.phone || "").replace(/[^\d+]/g, "").replace(/^\+/, "");
                            const waLink = rawPhone ? `https://wa.me/${rawPhone}?text=${message}` : null;
                            return (
                                <div key={`${b.customer_id}-${b.kind}`} className="rounded-lg bg-white/70 backdrop-blur-sm border border-gold-200 p-3 flex items-center gap-3">
                                    <span className="text-2xl">{kindEmoji}</span>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-slate-900 truncate">{b.name}</div>
                                        <div className="text-[11px] text-gold-700 font-mono">{kindLabel} · {b.date} · {dayText}</div>
                                    </div>
                                    {waLink ? (
                                        <a
                                            href={waLink}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`whatsapp-${b.customer_id}-${b.kind}`}
                                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium transition-colors"
                                            title="用 WhatsApp 发送祝福"
                                        >
                                            <MessageCircle className="h-3.5 w-3.5" />
                                            <span className="hidden lg:inline">WhatsApp</span>
                                        </a>
                                    ) : (
                                        <span className="text-[10px] text-slate-400">无电话</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {birthdays.length > 6 && (
                        <div className="text-xs text-gold-600 text-center mt-3">还有 {birthdays.length - 6} 位客户在近期有生日 / 纪念日 · <a href="/customers" className="underline hover:text-gold-800">查看全部</a></div>
                    )}
                </Card>
            )}

            <div className="grid grid-cols-1 gap-4">
                <Card className="p-6 border-slate-200 shadow-sm">
                    <div className="flex items-baseline justify-between mb-4">
                        <div>
                            <h2 className="text-lg font-semibold tracking-tight text-slate-900">营收趋势</h2>
                            <div className="text-xs text-gold-500 mt-0.5">
                                {range?.from ? "所选日期区间" : "默认最近 30 天"} · 单位：RM 马币
                            </div>
                        </div>
                    </div>
                    <div style={{ width: "100%", height: 260 }}>
                        <ResponsiveContainer width="100%" height={260} minWidth={0} minHeight={260}>
                            <LineChart data={trendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
                                <YAxis stroke="#64748B" fontSize={11} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: "#0F172A", border: "none", borderRadius: 6, color: "white" }}
                                    formatter={(v) => [formatMYR(v), "营收"]}
                                />
                                <Line type="monotone" dataKey="revenue" stroke="#B08A3E" strokeWidth={2} dot={false} activeDot={{ r: 5 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
            </div>

            <Card className="p-6 border-slate-200 shadow-sm" data-testid="platform-breakdown-card">
                <div className="flex items-baseline justify-between mb-4">
                    <div>
                        <h2 className="text-lg font-semibold tracking-tight text-slate-900">各平台营收占比</h2>
                        <div className="text-xs text-gold-500 mt-0.5">按销售平台聚合的总营收</div>
                    </div>
                </div>
                {platform.length === 0 ? (
                    <div className="py-12 text-center text-sm text-gold-500">暂无平台数据</div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-center">
                        <div className="lg:col-span-2" style={{ width: "100%", height: 280 }}>
                            <ResponsiveContainer width="100%" height={280} minWidth={0} minHeight={280}>
                                <PieChart>
                                    <Pie
                                        data={platform}
                                        dataKey="revenue"
                                        nameKey="name"
                                        innerRadius={60}
                                        outerRadius={100}
                                        paddingAngle={2}
                                        stroke="#ffffff"
                                        strokeWidth={2}
                                    >
                                        {platform.map((p, i) => (
                                            <Cell key={p.name} fill={PLATFORM_COLORS[i % PLATFORM_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{ backgroundColor: "#0F172A", border: "none", borderRadius: 6, color: "white" }}
                                        formatter={(v, n) => [formatMYR(v), n]}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="lg:col-span-3 space-y-2">
                            {platform.map((p, i) => {
                                const pct = ((p.revenue / platformTotal) * 100).toFixed(1);
                                return (
                                    <div key={p.name} data-testid={`platform-row-${p.name}`} className="flex items-center gap-3">
                                        <span
                                            className="h-3 w-3 rounded-sm shrink-0"
                                            style={{ backgroundColor: PLATFORM_COLORS[i % PLATFORM_COLORS.length] }}
                                        />
                                        <span className="text-sm font-medium text-slate-900 min-w-[100px]">{p.name}</span>
                                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                            <div
                                                className="h-full rounded-full transition-all"
                                                style={{ width: `${pct}%`, backgroundColor: PLATFORM_COLORS[i % PLATFORM_COLORS.length] }}
                                            />
                                        </div>
                                        <span className="text-sm font-mono font-semibold text-slate-900 w-24 text-right">{formatMYR(p.revenue)}</span>
                                        <span className="text-xs font-mono text-gold-500 w-12 text-right">{pct}%</span>
                                        <span className="text-xs font-mono text-slate-400 w-16 text-right">{p.count} 单</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </Card>

            <Card className="border-slate-200 shadow-sm">
                <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-semibold tracking-tight text-slate-900">低库存预警</h2>
                        <div className="text-xs text-gold-500 mt-0.5">达到或低于告警阈值的产品</div>
                    </div>
                    <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">
                        {data.low_stock_items.length} 项
                    </Badge>
                </div>
                {data.low_stock_items.length === 0 ? (
                    <div className="p-8 text-center text-sm text-gold-500">全部库存充足，做得好！</div>
                ) : (
                    <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-slate-200">
                                <th className="text-left py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">产品</th>
                                <th className="text-left py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">SKU</th>
                                <th className="text-right py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">库存</th>
                                <th className="text-right py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">阈值</th>
                                <th className="text-right py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">单价</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.low_stock_items.map((p) => (
                                <tr key={p.id} className="border-b border-slate-100 hover:bg-gold-50 transition-colors duration-150">
                                    <td className="py-3 px-6 text-sm font-medium text-slate-900">{p.name}</td>
                                    <td className="py-3 px-6 text-sm text-slate-600 font-mono">{p.sku}</td>
                                    <td className="py-3 px-6 text-sm text-right font-mono text-red-600 font-semibold">{p.stock}</td>
                                    <td className="py-3 px-6 text-sm text-right font-mono text-slate-600">{p.low_stock_threshold}</td>
                                    <td className="py-3 px-6 text-sm text-right font-mono text-slate-900">{formatMYR(p.unit_price)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                )}
            </Card>
        </div>
    );
}

function TodoCard({ testId, to, icon: Icon, label, count, hint, accent, emptyAccent }) {
    const isEmpty = !count;
    const badgeClass = isEmpty ? emptyAccent : accent;
    return (
        <Link
            to={to}
            data-testid={testId}
            className="group block"
        >
            <Card className={`p-5 border shadow-sm hover:shadow-md transition-shadow duration-150 ${isEmpty ? "border-slate-200" : "border-gold-200"}`}>
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold uppercase tracking-wider text-gold-500 truncate">{label}</div>
                        <div className="text-3xl font-bold font-mono text-slate-900 mt-2">{count}</div>
                        <div className="text-xs text-gold-600 mt-1 truncate">{hint}</div>
                    </div>
                    <div className={`h-10 w-10 rounded-md border flex items-center justify-center shrink-0 ${badgeClass}`}>
                        <Icon className="h-4 w-4" />
                    </div>
                </div>
                <div className="mt-3 text-xs text-gold-500 group-hover:text-gold-700 inline-flex items-center gap-1 transition-colors">
                    查看详情 <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                </div>
            </Card>
        </Link>
    );
}
